import * as Sentry from '@sentry/sveltekit';
import { browser } from '$app/environment';
import {
	isAuthenticated,
	getTokenRemainingTime,
	shouldRefreshToken
} from '$lib/state/auth.state.svelte';
import { devLog } from './lib/utils/dev';
import type { HandleClientError } from '@sveltejs/kit';

/**
 * Sentry Initialization
 * Initialize Sentry on the client-side for error tracking and performance monitoring
 */
Sentry.init({
	dsn: import.meta.env.PUBLIC_SENTRY_DSN,

	// Environment detection
	environment: import.meta.env.MODE || 'development',

	// Performance monitoring
	tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,

	// Session replay for debugging
	replaysSessionSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
	replaysOnErrorSampleRate: 1.0,

	// Enable integrations
	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.replayIntegration({
			maskAllText: true,
			blockAllMedia: true
		})
	],

	// Enable source maps in production
	enabled: import.meta.env.MODE === 'production',

	// Ignore known non-critical errors
	ignoreErrors: [
		// Browser extensions
		'ResizeObserver loop',
		'Non-Error promise rejection',
		// Network errors that don't need tracking
		'NetworkError',
		'Failed to fetch'
	]
});

/**
 * Register hooks to run on the client-side before SvelteKit handles a request
 */
export const handleNavigate = () => {
	// Services are now registered in +layout.svelte at module load time
	if (browser) {
		// Check token on every navigation
		if (isAuthenticated()) {
			const remainingMs = getTokenRemainingTime();
			devLog(`[Navigation] Checking token: ${Math.floor(remainingMs / 60000)} minutes remaining`);

			// If less than 10 minutes remaining, refresh proactively
			if (remainingMs < 10 * 60 * 1000 || shouldRefreshToken()) {
				devLog('[Navigation] Refreshing token during navigation');
				window.dispatchEvent(new CustomEvent('auth:refresh-token-needed'));
			}
		}
	}

	// Continue with navigation
	return;
};

/**
 * Handle client-side errors with Sentry integration
 * This captures unhandled errors and sends them to Sentry
 */
export const handleError: HandleClientError = ({ error, event, status, message }) => {
	// Log error details for debugging
	devLog('[Client Error Handler]', { error, status, message, url: event.url.pathname });

	// Send to Sentry with additional context
	Sentry.captureException(error, {
		contexts: {
			sveltekit: {
				event: event.url.pathname,
				status,
				message
			}
		},
		tags: {
			component: 'client',
			status: status.toString()
		}
	});

	// Return error details to SvelteKit
	return {
		message:
			import.meta.env.MODE === 'production'
				? 'An unexpected error occurred'
				: message || 'Unknown error'
	};
};
